import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import SpreadsheetEditor from '@/modules/Logistics/components/SpreadsheetEditor.vue';
import { getDeliveryListColumnIndex } from '@/modules/Logistics/helpers/LogisticsParserHelper';
import type {
    SpreadsheetData,
    DeliveryListFormData,
    DeliveryListPrintData,
    VehicleDropdownOption,
    MontageTeamDropdownOption,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';


export default /*#__PURE__*/_defineComponent({
  __name: 'DeliveryList',
  props: {
    printData: null,
    montageTeams: null,
    vehicles: null,
    totalPieces: null
  },
  emits: ["update:formField", "spreadsheet-change", "print-loading-list", "print-delivery-list"],
  setup(__props: any, { emit }: { emit: ({
    (
        e: 'update:formField',
        field: keyof DeliveryListFormData,
        value: DeliveryListFormData[keyof DeliveryListFormData],
    ): void;
    (e: 'spreadsheet-change', data: SpreadsheetData | null): void;
    (e: 'print-loading-list'): void;
    (e: 'print-delivery-list'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    printData: DeliveryListPrintData;
    montageTeams: MontageTeamDropdownOption[];
    vehicles: VehicleDropdownOption[];
    totalPieces: number;
};



const piecesColumnIndex = getDeliveryListColumnIndex('quantity');



// Computed access to form data
const formData = computed(() => props.printData.formData);

// Helper to get driver name from MontageTeam
function getDriverName(team: MontageTeamDropdownOption): string {
    return `${team.name || ''} ${team.lastName || ''}`.trim();
}

// Selected vehicle - maps to dropdown or keeps original string if no match
const selectedVehicle = computed(() => {
    const currentValue = formData.value?.vehicle;
    if (!currentValue) return undefined;
    const match = props.vehicles.find((v) => v.registration === currentValue);
    return match ? match.registration : currentValue;
});

// Selected driver - maps to dropdown or keeps original string if no match
const selectedDriver = computed(() => {
    const currentValue = formData.value?.driver;
    if (!currentValue) return undefined;
    const match = props.montageTeams.find((t) => getDriverName(t) === currentValue);
    return match ? getDriverName(match) : currentValue;
});

// Filter option for searchable selects
function filterOption(input: string, option: any): boolean {
    const label = option.componentOptions?.children?.[0]?.text || '';
    return label.toLowerCase().includes(input.toLowerCase());
}

// Handle vehicle selection change
function handleVehicleChange(value: string | undefined) {
    emit('update:formField', 'vehicle', value || '');
}

// Handle driver selection change
function handleDriverChange(value: string | undefined) {
    emit('update:formField', 'driver', value || '');
}

// Handle overall comment change
function handleOverallCommentChange(value: string) {
    emit('update:formField', 'overallComment', value || '');
}

// Convert spreadsheet data to table headers
const tableHeaders = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows?.[0]?.cells) return [];

    const headerRow = data.rows[0].cells;
    const headers: string[] = [];
    const cellKeys = Object.keys(headerRow)
        .map(Number)
        .sort((a, b) => a - b);

    for (const key of cellKeys) {
        headers.push(headerRow[key]?.text || '');
    }

    return headers;
});

// Convert spreadsheet data to table rows (excluding header)
const tableRows = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows) return [];

    const rows: Array<Array<{ text: string; style?: number }>> = [];
    const rowKeys = Object.keys(data.rows)
        .map(Number)
        .sort((a, b) => a - b);
    const colCount = tableHeaders.value.length;

    // Skip row 0 (header)
    for (const rowKey of rowKeys) {
        if (rowKey === 0) continue;

        const rowData = data.rows[rowKey];
        if (!rowData?.cells) continue;

        const row: Array<{ text: string; style?: number }> = [];
        for (let col = 0; col < colCount; col++) {
            row.push({
                text: rowData.cells[col]?.text || '',
                style: rowData.cells[col]?.style,
            });
        }
        rows.push(row);
    }

    return rows;
});

function handleDataChange(data: SpreadsheetData | null) {
    emit('spreadsheet-change', data);
}

return { __sfc: true,props, piecesColumnIndex, emit, formData, getDriverName, selectedVehicle, selectedDriver, filterOption, handleVehicleChange, handleDriverChange, handleOverallCommentChange, tableHeaders, tableRows, handleDataChange, SpreadsheetEditor }
}

})